import React, { useEffect, useState } from 'react';
import { getAllLiveLocations } from '../api/workerLocationAPI';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import UserDashboard from './UserDashboard';
// Fix default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const RecenterMap = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.setView([lat, lng], 14);
  }, [lat, lng, map]);
  return null;
};

const LiveLocationDashboardmyo = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [highlightedLocation, setHighlightedLocation] = useState(null);

  const fetchLocations = async () => {
    try {
      const res = await getAllLiveLocations();
      setLocations(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch locations', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    const interval = setInterval(fetchLocations, 60000);
    return () => clearInterval(interval);
  }, []);

  const applyJitter = (lat, lng, factor = 0.0003) => {
    const jitterLat = lat + (Math.random() - 0.5) * factor;
    const jitterLng = lng + (Math.random() - 0.5) * factor;
    return [jitterLat, jitterLng];
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearch(term);
    if (term.trim() === '') {
      setHighlightedLocation(null);
      return;
    }
    const found = locations.find((loc) =>
      loc?.worker?.username?.toLowerCase().includes(term.toLowerCase())
    );
    setHighlightedLocation(found || null);
  };

  const handleSelectWorker = (loc) => {
    setHighlightedLocation(loc);
  };

  const center = highlightedLocation
    ? { lat: highlightedLocation.latitude, lng: highlightedLocation.longitude }
    : locations[0]
    ? { lat: locations[0].latitude, lng: locations[0].longitude }
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-100 to-pink-100 pt-24 px-4">
         <UserDashboard />
      
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 mt-2">
        <h2 className="text-3xl font-bold text-purple-800 mb-2 md:mb-0">
          Worker Live Location
        </h2>
        <input
          type="text"
          placeholder="Search by worker name..."
          value={search}
          onChange={handleSearch}
          className="border rounded-md p-2 w-full md:w-96 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
        />
      </div>

      {loading ? (
        <p>⏳ Loading live locations...</p>
      ) : locations.length === 0 ? (
        <p className="text-gray-500">No worker locations available yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Left Panel - Map */}
          <div className="w-full h-[500px] border rounded-lg overflow-hidden shadow">
            {center ? (
              <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%', zIndex: 0 }}>
                <RecenterMap lat={center.lat} lng={center.lng} />
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                />
                {locations.map((loc) => {
                  const [jitterLat, jitterLng] = applyJitter(loc.latitude, loc.longitude);
                  const isHighlighted = highlightedLocation?._id === loc._id;
                  return (
                    <Marker
                      key={loc._id}
                      position={isHighlighted ? [loc.latitude, loc.longitude] : [jitterLat, jitterLng]}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="flex items-center space-x-2 mb-2">
                            <img
                              src={loc?.profileImage}
                              alt="Profile"
                              className="w-8 h-8 rounded-full border"
                            />
                            <strong>{loc?.worker?.username || 'Unknown'}</strong>
                          </div>
                          <div>
                            📍Lat: {loc.latitude.toFixed(5)}<br />
                            📍Lng: {loc.longitude.toFixed(5)}<br />
                            🕒 Updated: {new Date(loc.updatedAt).toLocaleTimeString()}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No location data to display on map.
              </div>
            )}
          </div>

          {/* Right Panel - Styled Table */}
          <div className="w-full max-h-[500px] overflow-y-auto rounded-lg shadow bg-white">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-purple-100 z-10">
                <tr className="bg-purple-100 text-purple-800 font-semibold">
                  <th className="p-3">Profile</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Role</th>
                  <th className="p-3">Latitude</th>
                  <th className="p-3">Longitude</th>
                  <th className="p-3">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((loc) => (
                  <tr
                    key={loc._id}
                    className={`border-b hover:bg-purple-50 cursor-pointer transition ${
                      highlightedLocation?._id === loc._id ? 'bg-purple-200' : ''
                    }`}
                    onClick={() => handleSelectWorker(loc)}
                  >
                    <td className="p-3">
                      <img
                        src={loc.profileImage}
                        alt="profile"
                        className="w-10 h-10 rounded-full object-cover border"
                      />
                    </td>
                    <td className="p-3 font-semibold text-purple-700">
                      {loc?.worker?.username || 'Unknown'}
                    </td>
                    <td className="p-3 text-purple-700">
  {loc?.role || loc?.worker?.role || 'Unknown'}
</td>
                    <td className="p-3">{loc.latitude.toFixed(5)}</td>
                    <td className="p-3">{loc.longitude.toFixed(5)}</td>
                    <td className="p-3 text-sm text-gray-500">
                      {new Date(loc.updatedAt).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      )}
    </div>
  );
};

export default LiveLocationDashboardmyo;
